import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const BlockManagementSection = ({ userId }) => {
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockUserId, setBlockUserId] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const blockedUsers = [
    {
      id: 1,
      userId: 'User #3421',
      blockedDate: '2026-01-22',
      reason: 'Inappropriate messages'
    },
    {
      id: 2,
      userId: 'User #7854',
      blockedDate: '2026-01-18',
      reason: 'Harassment'
    }
  ];

  const handleBlockUser = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('User blocked successfully.');
      setShowBlockForm(false);
      setBlockUserId('');
      setBlockReason('');
    } catch (error) {
      console.error('Block user error:', error);
      alert('Failed to block user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblockUser = async (blockedUserId) => {
    if (!confirm('Are you sure you want to unblock this user?')) return;

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('User unblocked successfully.');
    } catch (error) {
      console.error('Unblock user error:', error);
      alert('Failed to unblock user. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">Blocked Users</h2>
          <p className="text-sm text-muted-foreground">Manage users you've blocked</p>
        </div>
        <Button
          onClick={() => setShowBlockForm(!showBlockForm)}
          iconName="Ban"
          variant={showBlockForm ? "outline" : "default"}
        >
          {showBlockForm ? 'Cancel' : 'Block User'}
        </Button>
      </div>

      {/* Block Form */}
      {showBlockForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Block a User</h3>
          <form onSubmit={handleBlockUser} className="space-y-4">
            <Input
              label="User ID"
              placeholder="Enter user ID or profile link"
              value={blockUserId}
              onChange={(e) => setBlockUserId(e?.target?.value)}
              required
              description="You can find the user ID in their profile URL"
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Reason (Optional)</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Why are you blocking this user?"
                value={blockReason}
                onChange={(e) => setBlockReason(e?.target?.value)}
              />
            </div>

            <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
              <div className="flex gap-2">
                <Icon name="Info" size={16} className="text-warning mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Blocking a user will prevent them from viewing your profile, sending you messages, or interacting with you on the platform.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={submitting} variant="warning" fullWidth>
                Block User
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowBlockForm(false)} fullWidth>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Blocked Users List */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Currently Blocked</h3>
        
        {blockedUsers?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Ban" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No blocked users</p>
          </div>
        ) : (
          <div className="space-y-3">
            {blockedUsers?.map((blocked) => (
              <div key={blocked?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-error/10 rounded-full">
                    <Icon name="Ban" size={20} className="text-error" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{blocked?.userId}</h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        <span>Blocked on {new Date(blocked?.blockedDate)?.toLocaleDateString()}</span>
                      </div>
                      {blocked?.reason && (
                        <div className="flex items-center gap-1">
                          <Icon name="MessageSquare" size={12} />
                          <span>{blocked?.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleUnblockUser(blocked?.id)}
                  iconName="Unlock"
                >
                  Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Privacy Controls */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Block Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Auto-block reported users</p>
              <p className="text-xs text-muted-foreground">Automatically block users you report</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Hide from blocked users</p>
              <p className="text-xs text-muted-foreground">Prevent blocked users from seeing you existed</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockManagementSection;